import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  PropsWithChildren,
  memo,
} from 'react';
import { useLocation } from 'react-router-dom';
import { UseLocationType } from 'utility/types/cosmosTypes';
import { v7 as uuidv7 } from 'uuid';

/**
 * Unique runtime ID string for this app instance,
 * current primary use is for ydoc collab presence object
 * keys so we can track same user but multiple tabs/devices.
 *
 * not using session storage:
 * - Chrome copies sessionStorage if the page is duplicated.
 * - The sessionStorage persists across a hard reload.
 *
 * not using named export from file:
 * - eg export const runtimeId = generateId(...)
 * - could change across hmr and bundler chunks
 *
 * Note: this may change in dev mode across
 * hot module reloads, but in a production
 * build it should remain stable during runtime.
 * eg: even when switching sites, it should
 * remain stable until the app is hard refreshed.
 *
 * https://react.dev/reference/react/useState#avoiding-recreating-the-initial-state
 *
 */

function generateRuntimeId(): string {
  const uuid = uuidv7(); // unix time-based
  const runId = `runtime::${uuid}`;
  return runId;
}

export type CosmosRuntimeState = {
  /**
   * Unique runtime identifier for this apps instance
   */
  runtimeId: string;
  /**
   * Array of the last 50 `location = useLocation()` entries
   * note: The last item in the array is the current location.
   */
  history: UseLocationType[];
  /**
   * Undefined if there is no history
   * (e.g. first page, directly navigated to)
   */
  lastLocation: UseLocationType | undefined;
};

const CosmosRuntimeContext = createContext<CosmosRuntimeState | undefined>(undefined);

export const useCosmosRuntime = () => {
  const context = useContext(CosmosRuntimeContext);
  if (!context) {
    throw new Error('useCosmosRuntime must be used within a CosmosRuntimeProvider');
  }
  return context;
};

export const CosmosRuntimeProvider = memo(({ children }: PropsWithChildren) => {
  const location = useLocation();
  const [runtimeId] = useState<string>(generateRuntimeId);
  const [history, setHistory] = useState<UseLocationType[]>([]);

  useEffect(() => {
    // Keep the last 50 entries, should be enough and still be performant.
    setHistory((prevHistory) => [...prevHistory.slice(-49), location]);
  }, [location]);

  const contextValue = useMemo(() => {
    return {
      runtimeId: runtimeId,
      history: history,
      lastLocation: history[history.length - 2],
    };
  }, [runtimeId, history]);

  return (
    <CosmosRuntimeContext.Provider value={contextValue}>
      {children}
    </CosmosRuntimeContext.Provider>
  );
});
CosmosRuntimeProvider.displayName = 'CosmosRuntimeProvider';
